import { STEP_IDS } from "../hooks/use-onboarding-flow";
import type { InviteDraft, OnboardingStepId } from "../types";

export interface StructureViolations {
	company_name?: string;
	name?: string;
	street?: string;
	number?: string;
	complement?: string;
	neighborhood?: string;
	city?: string;
	state?: string;
	zipcode?: string;
	area?: string;
	status?: string;
	type?: string;
	color?: string;
	startDate?: string;
	forecastEnd?: string;
}

export type TeamViolations = Record<string, { email?: string; role?: string }>;

export interface AccountViolations {
	name?: string;
	email?: string;
	password?: string;
}

export function stepForViolationField(field: string): OnboardingStepId | null {
	if (field === "profile") return "profile";
	if (field === "mode") return "mode";
	if (field === "company_name") return "structure";
	if (field.startsWith("site.") || field.startsWith("construction.")) {
		return "structure";
	}
	if (/^invites\[\d+\]/.test(field)) return "team";
	return null;
}

export function earliestViolationStep(
	violations: Record<string, string>,
): OnboardingStepId | null {
	let best: OnboardingStepId | null = null;
	let bestIndex = Number.POSITIVE_INFINITY;

	for (const field of Object.keys(violations)) {
		const step = stepForViolationField(field);
		if (!step) continue;
		const index = STEP_IDS.indexOf(step);
		if (index >= 0 && index < bestIndex) {
			bestIndex = index;
			best = step;
		}
	}

	return best;
}

const STRUCTURE_FIELD_ALIASES: Record<string, keyof StructureViolations> = {
	"construction.start_date": "startDate",
	"construction.end_date": "forecastEnd",
};

export function scopeStructureViolations(
	violations: Record<string, string>,
): StructureViolations {
	const scoped: StructureViolations = {};

	for (const [field, message] of Object.entries(violations)) {
		if (field === "company_name") {
			scoped.company_name = message;
			continue;
		}

		const alias = STRUCTURE_FIELD_ALIASES[field];
		if (alias) {
			scoped[alias] = message;
			continue;
		}

		const local = field.replace(/^(site|construction)\./, "");
		if (local !== field) {
			scoped[local as keyof StructureViolations] = message;
		}
	}

	return scoped;
}

export function scopeAccountViolations(
	violations: Record<string, string>,
): AccountViolations {
	const scoped: AccountViolations = {};

	for (const [field, message] of Object.entries(violations)) {
		const key = field.split(".")[0];
		if (key === "name" || key === "email" || key === "password") {
			scoped[key] = message;
		}
	}

	return scoped;
}

export function scopeTeamViolations(
	violations: Record<string, string>,
	invites: InviteDraft[],
): TeamViolations {
	const scoped: TeamViolations = {};

	for (const [field, message] of Object.entries(violations)) {
		const match = field.match(/^invites\[(\d+)\]\.(email|role)$/);
		if (!match) continue;

		const invite = invites[Number(match[1])];
		if (!invite) continue;

		const key = match[2] as "email" | "role";
		scoped[invite.id] = { ...scoped[invite.id], [key]: message };
	}

	return scoped;
}
